/**
 * Realtime Channel Hook
 * 
 * A reusable hook to subscribe to Supabase Realtime channels.
 * Handles subscription lifecycle, including cleanup.
 */

import { useEffect, useRef } from 'react';

import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';

type PostgresEventType = 'INSERT' | 'UPDATE' | 'DELETE' | '*';
type EventCallback<T> = (payload: RealtimePostgresChangesPayload<T> | { event: string; payload: T }) => void;

interface PostgresChannelOptions<T> {
  channelName: string;
  type: 'postgres_changes';
  event: PostgresEventType;
  schema?: string;
  table?: string;
  filter?: string;
  onMessage: EventCallback<T>;
}

interface BroadcastChannelOptions<T> {
  channelName: string;
  type: 'broadcast';
  event: string; // Custom event name
  onMessage: EventCallback<T>;
}

type RealtimeChannelOptions<T> = PostgresChannelOptions<T> | BroadcastChannelOptions<T>;

/**
 * A hook to subscribe to a Supabase Realtime channel.
 * Handles subscription lifecycle, including cleanup.
 *
 * @param options - Configuration for the channel subscription.
 * 
 * @example
 * // Subscribe to database changes
 * useRealtimeChannel({
 *   channelName: 'messages:session-123',
 *   event: 'INSERT',
 *   schema: 'public',
 *   table: 'messages',
 *   filter: 'session_id=eq.session-123',
 *   onMessage: (payload) => {
 *     console.log('New message:', payload.new);
 *   }
 * });
 * 
 * @example
 * // Subscribe to broadcast events
 * useRealtimeChannel({
 *   channelName: 'private-notifications:abc123',
 *   event: 'broadcast',
 *   onMessage: (payload) => {
 *     console.log('Broadcast received:', payload);
 *   }
 * });
 */
export const useRealtimeChannel = <T extends { [key: string]: any }>(
  options: RealtimeChannelOptions<T>
) => {
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!options.channelName) {
      console.warn('[Realtime] No channel name provided, skipping subscription');
      return;
    }

    // Create a new channel instance
    channelRef.current = supabase.channel(options.channelName);

    // Set up the subscription based on event type
    if (options.type === 'broadcast') {
      // Broadcast channel
      channelRef.current.on(
        'broadcast',
        { event: options.event },
        (payload) => {
          options.onMessage(payload as any);
        }
      );
    } else {
      // Postgres changes channel
      const pgOptions = options as PostgresChannelOptions<T>;
      channelRef.current = channelRef.current.on(
        'postgres_changes' as any,
        {
          event: pgOptions.event,
          schema: pgOptions.schema || 'public',
          table: pgOptions.table,
          filter: pgOptions.filter,
        } as any,
        (payload) => {
          options.onMessage(payload as any);
        }
      );
    }

    // Subscribe to the channel
    channelRef.current.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[Realtime] Subscribed to ${options.channelName}`);
      }
      if (status === 'CHANNEL_ERROR' || err) {
        console.error(`[Realtime] Error on channel ${options.channelName}:`, err);
      }
      if (status === 'TIMED_OUT') {
        console.warn(`[Realtime] Subscription timed out for ${options.channelName}`);
      }
      if (status === 'CLOSED') {
        console.log(`[Realtime] Channel ${options.channelName} closed`);
      }
    });

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        console.log(`[Realtime] Unsubscribed from ${options.channelName}`);
      }
    };
  }, [options.channelName, options.type, (options as any).event, (options as any).schema, (options as any).table, (options as any).filter, options.onMessage]);
};
